import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Activity, Zap, Clock, Database } from 'lucide-react';

interface PerformanceMetrics {
  pageLoadTime: number;
  domContentLoaded: number;
  firstContentfulPaint: number;
  memoryUsage: number;
  connectionType: string;
  isOnline: boolean;
}

const PerformanceMonitor = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);

  useEffect(() => {
    const measurePerformance = () => {
      if (typeof window !== 'undefined' && window.performance) {
        const timing = window.performance.timing;
        const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
        
        // Get FCP if available
        let firstContentfulPaint = 0;
        const paintEntries = window.performance.getEntriesByType('paint');
        const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
        if (fcpEntry) {
          firstContentfulPaint = fcpEntry.startTime;
        }

        // Memory usage (if available)
        let memoryUsage = 0;
        if ('memory' in window.performance) {
          const memory = (window.performance as any).memory;
          memoryUsage = (memory.usedJSHeapSize / memory.totalJSHeapSize) * 100;
        }

        // Connection information
        let connectionType = 'Unknown';
        if ('connection' in navigator) {
          const connection = (navigator as any).connection;
          connectionType = connection.effectiveType || connection.type || 'Unknown';
        }

        setMetrics({
          pageLoadTime: Math.round(pageLoadTime),
          domContentLoaded: Math.round(domContentLoaded),
          firstContentfulPaint: Math.round(firstContentfulPaint),
          memoryUsage: Math.round(memoryUsage),
          connectionType,
          isOnline: navigator.onLine
        });
      }
    };

    // Measure after page load
    if (document.readyState === 'complete') {
      measurePerformance();
    } else {
      window.addEventListener('load', measurePerformance);
    }

    // Listen for online/offline changes
    const handleOnline = () => setMetrics(prev => prev ? { ...prev, isOnline: true } : null);
    const handleOffline = () => setMetrics(prev => prev ? { ...prev, isOnline: false } : null);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('load', measurePerformance);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!metrics) {
    return null;
  }

  const getLoadTimeStatus = (time: number) => {
    if (time < 1000) return { color: 'green', label: 'Excellent' };
    if (time < 2000) return { color: 'blue', label: 'Good' };
    if (time < 3000) return { color: 'orange', label: 'Fair' };
    return { color: 'red', label: 'Poor' };
  };

  const loadTimeStatus = getLoadTimeStatus(metrics.pageLoadTime);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Performance Metrics
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Page Load Time</span>
            </div>
            <div className="text-2xl font-bold">{metrics.pageLoadTime}ms</div>
            <Badge 
              variant="outline" 
              className={`text-${loadTimeStatus.color}-600 border-${loadTimeStatus.color}-200`}
            >
              {loadTimeStatus.label}
            </Badge>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">DOM Ready</span>
            </div>
            <div className="text-2xl font-bold">{metrics.domContentLoaded}ms</div>
            <div className="text-xs text-muted-foreground">
              DOM content loaded
            </div>
          </div>

          {metrics.firstContentfulPaint > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">First Paint</span>
              </div>
              <div className="text-2xl font-bold">{metrics.firstContentfulPaint}ms</div>
              <div className="text-xs text-muted-foreground">
                First contentful paint
              </div>
            </div>
          )}

          {metrics.memoryUsage > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Memory Usage</span>
              </div>
              <div className="space-y-2">
                <Progress value={metrics.memoryUsage} className="h-2" />
                <div className="text-sm text-muted-foreground">
                  {metrics.memoryUsage}% used
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Connection</span>
            <Badge variant="outline">{metrics.connectionType}</Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Status</span>
            <Badge variant={metrics.isOnline ? "default" : "destructive"}>
              {metrics.isOnline ? "Online" : "Offline"}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceMonitor;